import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { BellRing, CheckCircle2, Clock, Eye, Loader2, MailCheck, Trash2, XCircle } from 'lucide-react';
import { Avatar, AvatarFallback } from './Avatar';
import { Badge } from './Badge';
import { Button } from './Button';
import { EmiratiBadge } from './EmiratiBadge';
import { useEvents } from '../contexts/EventsContext';
import { experts } from '../data/experts';
import type { BakEvent, InvitationStatus } from '../types/event';
import { formatAED, getInitials } from '../utils/expert';
import { getTier } from '../utils/tier';
import { TierBadge } from './TierBadge';
import { formatDateTime, invitationStatusStyle, isAwaiting } from '../utils/event';
type Filter = 'all' | 'accepted' | 'awaiting' | 'declined';
type InvitationListProps = {
  event: BakEvent;
};
const statusIcon: Record<InvitationStatus, React.ReactNode> = {
  Sending: <Loader2 className="size-3 animate-spin" aria-hidden="true" data-id="element-348" data-mp="2h963w6mnj9rb" />,
  Delivered: <MailCheck className="size-3" aria-hidden="true" data-id="element-349" data-mp="ygpjp77hyee4" />,
  Viewed: <Eye className="size-3" aria-hidden="true" data-id="element-350" data-mp="thlaigkkogns" />,
  Accepted: <CheckCircle2 className="size-3" aria-hidden="true" data-id="element-351" data-mp="tf09fts3m4r3" />,
  Declined: <XCircle className="size-3" aria-hidden="true" data-id="element-352" data-mp="186raw7v5ccx9" />
};
export function InvitationList({
  event
}: InvitationListProps) {
  const {
    remindExpert,
    withdrawInvitation
  } = useEvents();
  const [filter, setFilter] = useState<Filter>('all');
  const counts = {
    all: event.invitations.length,
    accepted: event.invitations.filter(i => i.status === 'Accepted').length,
    awaiting: event.invitations.filter(i => isAwaiting(i.status)).length,
    declined: event.invitations.filter(i => i.status === 'Declined').length
  };
  const visible = event.invitations.filter(i => {
    if (filter === 'accepted') return i.status === 'Accepted';
    if (filter === 'declined') return i.status === 'Declined';
    if (filter === 'awaiting') return isAwaiting(i.status);
    return true;
  });
  if (event.invitations.length === 0) {
    return <div className="flex flex-col items-center rounded-xl border border-dashed bg-card py-14 text-center" data-id="element-353" data-mp="1wt275ebyl58n">
        <BellRing className="size-8 text-muted-foreground" aria-hidden="true" data-id="element-354" data-mp="20ux3e4f18sfk" />
        <p className="mt-3 text-sm font-medium" data-id="element-355" data-mp="20x04pklmy7zl">No experts invited yet</p>
        <p className="mt-1 max-w-sm text-xs text-muted-foreground" data-id="element-356" data-mp="25v1o66mz49bl">
          Use “Invite experts” to pick who you’d like to reach out to. They’ll be notified instantly.
        </p>
      </div>;
  }
  return <div className="rounded-xl border bg-card" data-id="element-357" data-mp="2fptk760wqo49">
      <div className="flex flex-wrap gap-1 border-b p-2" role="tablist" aria-label="Filter invitations" data-id="element-358" data-mp="2kiecu482uv67">
        {([['all', 'All'], ['accepted', 'Accepted'], ['awaiting', 'Awaiting reply'], ['declined', 'Declined']] as const).map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={filter === key} onClick={() => setFilter(key)} className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${filter === key ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-359" data-mp="2yjh0gleq0dj8">
            {label} <span className="ml-1 tabular-nums text-muted-foreground" data-id="element-360" data-mp="2unmkdxc19pv0">{counts[key]}</span>
          </button>)}
      </div>

      {visible.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground" data-id="element-361" data-mp="3me0vxmwkqq3i">No invitations in this view.</p> : <ul className="divide-y" data-id="element-362" data-mp="3hmirgovce7ds">
          <AnimatePresence initial={false} data-id="element-363" data-mp="3hlhh6mm5hox4">
            {visible.map(inv => {
          const expert = experts.find(e => e.id === inv.expertId);
          if (!expert) return null;
          return <motion.li key={inv.id} layout initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className="overflow-hidden" data-id="element-364" data-mp="e022arx2rgt6">
                  <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center" data-id="element-365" data-mp="3guyudcj3mrv5">
                    <div className="flex min-w-0 flex-1 items-center gap-3" data-id="element-366" data-mp="3kuqx38lta3sl">
                      <Avatar data-id="element-367" data-mp="cfz43xpon7a5"><AvatarFallback data-id="element-368" data-mp="cf76khciyzh8">{getInitials(expert.name)}</AvatarFallback></Avatar>
                      <div className="min-w-0" data-id="element-369" data-mp="heio3skpu8zv">
                        <div className="flex flex-wrap items-center gap-1.5" data-id="element-370" data-mp="heacs548rbqs">
                          <Link to={`/experts/${expert.id}`} className="truncate text-sm font-medium hover:underline" data-id="element-371" data-mp="3mcb2fcv0i4n">{expert.name}</Link>
                          {expert.isEmirati && <EmiratiBadge compact data-id="element-372" data-mp="vel5ldp430el" />}
                          <TierBadge tier={getTier(expert)} data-id="element-373" data-mp="vdruy1n17qr6" />
                          {getTier(expert).level >= 3 && <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800" data-id="element-374" data-mp="107e7zux3xwni">VIP treatment</span>}
                        </div>
                        <p className="text-xs text-muted-foreground" data-id="element-375" data-mp="2fuj7kvcoak4a">
                          {inv.role} · {formatAED(inv.proposedFee)} · Sent {formatDateTime(inv.sentAt)}
                        </p>
                        {inv.responseNote && <p className="mt-1 text-xs italic text-muted-foreground" data-id="element-376" data-mp="21w1cgckfu5cc">“{inv.responseNote}”</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:justify-end" data-id="element-377" data-mp="2buqrzyov3dpf">
                      <div className="text-right" data-id="element-378" data-mp="2bsnxp2goeyew">
                        <motion.div key={inv.status} initial={{
                    scale: 0.9,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} data-id="element-379" data-mp="26v4h3vwia9js">
                          <Badge variant="outline" className={`gap-1 ${invitationStatusStyle[inv.status]}`} data-id="element-380" data-mp="3n8clfw1r81gr">
                            {statusIcon[inv.status]}
                            {inv.status === 'Sending' ? 'Sending…' : inv.status}
                          </Badge>
                        </motion.div>
                        <p className="mt-1 flex items-center justify-end gap-1 text-[11px] text-muted-foreground" data-id="element-381" data-mp="3uf6vfeessu17">
                          {inv.respondedAt ? <>Replied {formatDateTime(inv.respondedAt)}</> : <><Clock className="size-3" aria-hidden="true" data-id="element-382" data-mp="36pch0setyxeu" />Awaiting reply</>}
                        </p>
                      </div>
                      {isAwaiting(inv.status) && inv.status !== 'Sending' && <Button variant="outline" size="sm" onClick={() => remindExpert(event.id, inv.id)} data-id="element-383" data-mp="3ld7e0kzvfnxq">
                          <BellRing data-id="element-384" data-mp="2ny3a8qwg2mqy" /> Remind
                        </Button>}
                      <Button variant="ghost" size="icon-sm" aria-label={`Withdraw invitation for ${expert.name}`} onClick={() => withdrawInvitation(event.id, inv.id)} data-id="element-385" data-mp="2svl7hk13ndj4">
                        <Trash2 data-id="element-386" data-mp="1102e7ij6ff0x" />
                      </Button>
                    </div>
                  </div>
                </motion.li>;
        })}
          </AnimatePresence>
        </ul>}
    </div>;
}